import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { usePageTitle } from '../../hooks/usePageTitle';

const ApproveSubscription: React.FC = () => {
  usePageTitle('Approve Subscription');
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'missing_params' | 'auth_required'>('loading');
  const [message, setMessage] = useState('');
  const [customerName, setCustomerName] = useState('');
  const { isAuthenticated, isLoading: authLoading, role, login } = useAuth();
  const authFetch = useAuthFetch();

  const subscriptionId = searchParams.get('id');
  const token = searchParams.get('token');

  useEffect(() => {
    if (authLoading) return;

    if (!subscriptionId || !token) {
      setStatus('missing_params');
      setMessage('Missing subscription ID or approval token. Please use the link from your email.');
      return;
    }

    // If not authenticated, prompt login (MSAL loginRedirect preserves the current URL)
    if (!isAuthenticated) {
      setStatus('auth_required');
      return;
    }

    // If authenticated but not admin, show error
    if (role !== 'admin') {
      setStatus('error');
      setMessage('You must be an admin to approve subscriptions. Please sign in with an admin account.');
      return;
    }

    const approve = async () => {
      try {
        const res = await authFetch(`/api/subscriptions/${subscriptionId}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const data = await res.json();

        if (res.ok) {
          setStatus('success');
          setCustomerName(data.subscription?.customerName || '');
          setMessage(data.message || 'Subscription approved successfully.');
        } else {
          setStatus('error');
          setMessage(data.message || 'Failed to approve subscription.');
        }
      } catch (err) {
        setStatus('error');
        setMessage('An error occurred while processing the approval. Please try again from the admin dashboard.');
      }
    };

    approve();
  }, [subscriptionId, token, authFetch, isAuthenticated, authLoading, role, login]);

  return (
    <div className="admin-manage-page">
      <section className="dashboard-header">
        <div className="section-container">
          <h1 className="page-title">Subscription Approval</h1>
        </div>
      </section>

      <section className="section">
        <div className="section-container">
          {(status === 'loading' || authLoading) && (
            <div className="loading-container">
              <div className="loading-spinner" />
              <p>Processing approval...</p>
            </div>
          )}

          {status === 'auth_required' && (
            <div className="alert alert-warning">
              <span className="alert-icon">&#128274;</span>
              <div>
                <strong>Sign In Required</strong>
                <p>You need to sign in as an admin to approve this subscription.</p>
                <button
                  className="btn btn-primary"
                  style={{ marginTop: '12px' }}
                  onClick={login}
                >
                  Sign In
                </button>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="alert alert-success" style={{ background: 'var(--green-light, #d4edda)', borderColor: 'var(--green, #28a745)', color: '#155724' }}>
              <span className="alert-icon">&#10003;</span>
              <div>
                <strong>Subscription Approved!</strong>
                {customerName && <p>{customerName}'s subscription has been activated.</p>}
                <p>The customer has been sent a welcome email and approval notification.</p>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="alert alert-warning">
              <span className="alert-icon">&#9888;</span>
              <div>
                <strong>Approval Failed</strong>
                <p>{message}</p>
              </div>
            </div>
          )}

          {status === 'missing_params' && (
            <div className="alert alert-warning">
              <span className="alert-icon">&#9888;</span>
              <div>
                <strong>Invalid Link</strong>
                <p>{message}</p>
              </div>
            </div>
          )}

          {status !== 'loading' && status !== 'auth_required' && !authLoading && (
            <div style={{ marginTop: '24px' }}>
              <Link to="/admin/subscriptions" className="btn btn-primary">
                Go to Manage Subscriptions
              </Link>
              <Link to="/admin" className="btn btn-outline" style={{ marginLeft: '12px' }}>
                Back to Dashboard
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ApproveSubscription;
